import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesApi } from "../services/api";

function CoachingOptionDetailPage() {
  const { id } = useParams();
  const [option, setOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOption();
  }, [id]);

  const fetchOption = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesApi.getCoachingOption(id);
      setOption(response.data);
    } catch (err) {
      console.error("Error loading coaching option:", err);
      setError("Coaching option not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !option) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-red-800 mb-4">
            Option Not Found
          </h1>
          <p className="text-red-600 mb-6">
            {error || "The coaching option you are looking for does not exist."}
          </p>
          <Link
            to="/coaching"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Back to Coaching
          </Link>
        </div>
      </div>
    );
  }

  const features = option.features
    ? option.features.split(",").map((f) => f.trim())
    : [];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F3A5F] to-[#3B82F6] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link
              to="/coaching"
              className="text-blue-100 hover:text-white transition"
            >
              ← Back to Coaching
            </Link>
          </div>
          <div className="flex items-start gap-6 mb-6">
            {option.icon && <div className="text-6xl">{option.icon}</div>}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {option.title}
              </h1>
              <p className="text-blue-100 text-xl">{option.description}</p>
            </div>
          </div>
          {option.duration && (
            <div className="bg-blue-500 bg-opacity-20 px-4 py-2 rounded-lg inline-block">
              <div className="text-sm text-blue-100">Duration</div>
              <div className="font-semibold">{option.duration}</div>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      {features.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-12 text-center">
              What's Included
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow p-6 flex items-start gap-4"
                >
                  <div className="text-blue-600 text-2xl mt-1">✓</div>
                  <div>
                    <p className="text-gray-700">{feature}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="mb-8 text-blue-100 text-lg">
            Book your coaching session and transform your career or business.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
          >
            Contact Us
          </Link>
        </div>
      </section>

      {/* Back to Coaching */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/coaching"
            className="text-blue-600 font-semibold hover:text-blue-700 transition"
          >
            ← View All Coaching Options
          </Link>
        </div>
      </section>
    </div>
  );
}

export default CoachingOptionDetailPage;
